import { useState, useMemo, MouseEvent, ChangeEvent } from 'react'
import { Modal } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { castArray } from 'lodash-es'

import Select from '@/ui/Select'
import Text from '@/ui/Text'
import Form from '@/ui/Form'
import { GlobalStyle, Search, Option, InputWrapper } from './Styles'

type Value = string | number | null

export interface MobileDropdownProps {
  value: Value | Value[]
  multiple?: boolean
  onChange: (
    e: MouseEvent<HTMLDivElement>,
    { value }: { value: Value | Value[] },
  ) => void
  options: {
    key: string
    value: string
    text: string
    textValue?: string
  }[]
  clearable?: boolean
  disabled?: boolean
}

const MobileDropdown = (props: MobileDropdownProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const filteredOptions = useMemo(
    () =>
      props.options?.filter(({ textValue, text }) =>
        (textValue ?? text)?.toLowerCase?.().includes(search.toLowerCase()),
      ) ?? [],
    [props.options, search],
  )

  const displaySearch = props.options ? props.options.length >= 10 : false

  const handleChange = (e: MouseEvent<HTMLDivElement>, value: Value) => {
    let newValue: Value | Value[] = value
    if (props.multiple) {
      const valueArray = castArray(props.value)
      if (valueArray.includes(value)) {
        newValue = valueArray.filter(v => v !== value)
      } else {
        newValue = [...valueArray, value]
      }
    }
    props.onChange(e, { value: newValue })

    if (!props.multiple) {
      setOpen(false)
      setSearch('')
    }
  }

  return (
    <>
      {open && <GlobalStyle />}

      <Modal open={open} onClose={() => setOpen(false)}>
        <Modal.Content>
          {displaySearch && (
            <Search
              value={search}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setSearch(e.target.value)
              }
              placeholder={t('Search')}
            />
          )}

          <div>
            {props.clearable && (
              <Option
                onClick={(e: MouseEvent<HTMLDivElement>) =>
                  handleChange(e, null)
                }>
                <Text.Span light>{t('None')}</Text.Span>
              </Option>
            )}
            {filteredOptions.map(({ value, text }) => (
              <Option
                key={value}
                onClick={(e: MouseEvent<HTMLDivElement>) =>
                  handleChange(e, value)
                }>
                <Text.Span light>{text}</Text.Span>

                {props.multiple && (
                  <Form.Checkbox
                    checked={castArray(props.value).includes(value)}
                  />
                )}
              </Option>
            ))}
          </div>
        </Modal.Content>
      </Modal>

      <InputWrapper
        disabled={props.disabled}
        onClick={() => {
          if (!props.disabled) {
            setOpen(true)
          }
        }}>
        {/* @ts-ignore */}
        <Select {...props} disabled />
      </InputWrapper>
    </>
  )
}

export default MobileDropdown
